import React, { useState, useEffect } from "react";
import { ProductPhysicalSelectEnum } from "../businessInformation/config";
import {
  Form,
  Input,
  Upload,
  InputNumber,
  Radio,
  Image,
  Alert,
  message,
} from "antd";
import { getProductLogoUploadUrl } from "@/api/createCampaignApi";
import axios from "axios";
import { CloseBgIcon, CloseCircleIcon, CloseIcon, DeleteIcon, InfoIcon, PlusIcon, UploadIcon } from "@/components/icons";
import LoadingLottie from "@/components/loading";
import { useTranslation } from "react-i18next";
import UploadImages from "@/components/CommonUploadImages";
import "./index.scss";

interface Props {
  form: any;
  productInfo: any;
  setProductInfo: Function;
  showPhotoError: boolean;
  setShowPhotoError: Function;
}

const ProductDelivery: React.FC<Props> = ({
  form,
  productInfo,
  setProductInfo,
  showPhotoError,
  setShowPhotoError,
}) => {
  const { t } = useTranslation();
  const { TextArea } = Input;
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);
  const getPhotoUpload = async (file: any) => {
    setPhotoUploadLoading(true);
    // 大小大于5MB
    if (file.size > 5 * 1024 * 1024) {
      message.error(t('campaignDetail.otherInformationPreview.fileSizeExceed5MB'));
      setPhotoUploadLoading(false);
      return;
    }

    try {
      const res = await getProductLogoUploadUrl({ key: file.name });
      const presignedUrl = res?.data?.presignedUrl;
      const publicUrl = res?.data?.publicUrl;
      const options = {
        headers: {
          "Content-Type": file.type,
        },
      };
      await axios.put(presignedUrl, file, options);
      setProductInfo({
        ...productInfo,
        physicalProductPhoto: publicUrl,
        physicalProductPhotoName: file?.name,
      });
      setShowPhotoError(false);
      setTimeout(() => {
        return Promise.resolve(presignedUrl);
      }, 0);
    } catch (error: any) {
      message.error(error?.message || t('campaignDetail.otherInformationPreview.uploadProductPhotoFailed'));
    } finally {
      setPhotoUploadLoading(false);
    }
  };

  useEffect(() => {
    setPhysicalProductShipType(
      productInfo?.physicalProductShipType || ProductPhysicalSelectEnum.ship
    );
  }, [productInfo?.physicalProductShipType]);

  const [physicalProductShipType, setPhysicalProductShipType] = useState(
    ProductPhysicalSelectEnum.ship
  );
  const handleRadioChange = (e: any) => {
    setPhysicalProductShipType(e.target.value);
  };
  const skuList = Form.useWatch('skuList', form) || [];

  return (
    <div className="mt-6">
      <div className="self-stretch justify-start text-[#1c2024] text-base font-medium  leading-normal mb-4">
        {t("campaignDetail.otherInformationPreview.productDelivery")}
      </div>
      <div className="product-basic-ship">
        <div className="product-basic-ship-header">
          <Form.Item
            name="physicalProductShipType"
            rules={[{ required: true, message: t('campaignDetail.otherInformationPreview.pleaseSelect') }]}
            className="mb-0"
          >
            <Radio.Group onChange={handleRadioChange}>
              <Radio value={ProductPhysicalSelectEnum.ship}>
                {t("campaignDetail.otherInformationPreview.iWillShipProduct")}
              </Radio>
              <Radio value={ProductPhysicalSelectEnum.video}>
                {t("campaignDetail.otherInformationPreview.noShipmentNeeded")}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </div>
        {physicalProductShipType === ProductPhysicalSelectEnum.ship && (
          <div className="product-basic-ship-content mt-4 border-t border-[#e5e5e5] pt-4">
            <div className="product-basic-ship-content-desc">
              {t("campaignDetail.otherInformationPreview.shipProductDescription")}
            </div>
            <div className="product-basic-ship-content-form">
              <div className="product-ship-from">
                <div className="product-ship-from-item">
                  <div className="product-ship-from-item-label">
                    <span>{t("campaignDetail.otherInformationPreview.productName")}</span>
                    <span className="required">*</span>
                  </div>
                  <Form.Item
                    name="physicalProductName"
                    rules={[
                      {
                        required: true,
                        message: t('campaignDetail.otherInformationPreview.pleaseEnterProductName'),
                      },
                    ]}
                  >
                    <Input maxLength={70} showCount size="large" />
                  </Form.Item>
                </div>
                <div className="product-ship-from-item relative">
                  <div className="product-ship-from-item-label">
                    <span>{t("campaignDetail.otherInformationPreview.productPhoto")}</span>
                    <span className="required">*</span>
                  </div>
                  <div className="product-ship-from-item-photo">
                    <div className="photo-container-left">
                      {productInfo?.physicalProductPhoto ? (
                        <div className="photo-container-left-upload">
                          <Image
                            width={20}
                            src={productInfo?.physicalProductPhoto}
                          />
                          <span>{productInfo?.physicalProductPhotoName}</span>
                        </div>
                      ) : (
                        <Upload
                          listType="picture"
                          accept=".jpg,.jpeg,.png"
                          maxCount={1}
                          showUploadList={false}
                          className="photo-uploader"
                          beforeUpload={getPhotoUpload}
                          rootClassName="photo-container-left-upload"
                          disabled={photoUploadLoading}
                        >
                          <div className="photo-container-left-upload">
                            {photoUploadLoading ? (
                              <LoadingLottie />
                            ) : (
                              <UploadIcon />
                            )}
                            <span>{t('campaignDetail.otherInformationPreview.uploadProductPhotos')}</span>
                          </div>
                        </Upload>
                      )}
                    </div>
                    <div className="photo-container-right">
                      {productInfo?.physicalProductPhoto ? (
                        <span
                          onClick={() => {
                            setProductInfo({
                              ...productInfo,
                              physicalProductPhoto: "",
                              physicalProductPhotoName: "",
                            });
                          }}
                        >
                          <CloseIcon />
                        </span>
                      ) : (
                        <span>{t('campaignDetail.otherInformationPreview.uploadPhotoHint')}</span>
                      )}
                    </div>
                  </div>
                  {showPhotoError && (
                    <div className="text-[#ff4d4f] absolute left-0 bottom-0">
                      {t('campaignDetail.otherInformationPreview.pleaseUploadProductPhotos')}
                    </div>
                  )}
                </div>
                <div className="product-ship-from-item">
                  <div className="product-ship-from-item-label">
                    <span>{t("campaignDetail.otherInformationPreview.productRetailPrice")}</span>
                    <span className="required">*</span>
                  </div>
                  <Form.Item
                    name="physicalProductCashValue"
                    rules={[
                      {
                        required: true,
                        message: t('campaignDetail.otherInformationPreview.pleaseEnterProductCashValue'),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      addonBefore="$"
                      min={0.01}
                      max={100000}
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="flex justify-start items-start gap-1.5 w-full">
                <InfoIcon />
                <div className="justify-start text-[#171717] text-sm font-normal  leading-snug">
                  {t('campaignDetail.otherInformationPreview.platformSubsidyDesc')}
                </div>
              </div>
              {/* product option */}
              <Form.List name="skuList">
                {(fields, { add, remove }) => (
                    <div className="flex flex-col gap-4 mt-4">
                      {fields.map((field) => {
                        const images = skuList[field.name]?.images || [];
                        return (
                        <div key={field.key} className="flex flex-col bg-white p-4 rounded-[8px] gap-6">
                          <div className="flex flex-col gap-[6px]">
                            <div className="text-[#44403C] text-sm font-medium font-['DM_Sans'] leading-5 flex items-center justify-between">
                              <span>{t('campaignDetail.otherInformationPreview.productOptionsForInfluencerSelection')}</span>
                              <div className="cursor-pointer" onClick={() => remove(field.name)}>
                                <DeleteIcon className="w-4 h-4 hover:text-primary" />
                              </div>
                            </div>
                            <Form.Item name={[field.name, 'label']} className="mb-0" rules={[{ required: true, message: t('campaignDetail.otherInformationPreview.pleaseEnterProductOptions') }]}>
                              <Input size="large" placeholder="Add product options influencers can select from (e.g. size, color, capacity)" />
                            </Form.Item>
                          </div>
                          <div className="flex flex-col gap-[6px]">
                            <div className="text-[#44403C] text-sm font-medium font-['DM_Sans'] leading-5 flex items-center justify-between">
                              <span>{t('campaignDetail.otherInformationPreview.optionalGuidanceForInfluencerSelection')}</span>
                              <UploadImages type="multiple" value={images} onChange={(files: any) => {
                                form.setFieldValue(['skuList', field.name, 'images'], files);
                              }}>
                                <div className="flex items-center gap-1 cursor-pointer">
                                  <UploadIcon className="w-[20px] h-[20px] text-[#79716B]" />
                                  <div className="justify-start text-[#79716B] text-sm font-semibold font-['DM_Sans'] leading-5">{t('campaignDetail.otherInformationPreview.addImage')}</div>
                                </div>
                              </UploadImages>
                            </div>
                            <Form.Item name={[field.name, 'description']} className="mb-0" rules={[{ required: true, message: t('campaignDetail.otherInformationPreview.pleaseEnterOptionalGuidance') }]}>
                              <TextArea
                                placeholder="Add guidance to help influencers make the right selection (e.g. sizing tips, color suggestions)"
                              />
                            </Form.Item>
                            {images.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {images.map((item: any, index: number) => (
                                  <div key={index} className="relative">
                                    <Image
                                      src={item.src}
                                      alt={item.fileName}
                                      width={56}
                                      height={56}
                                      className="object-cover rounded"
                                    />
                                    <div
                                      className="absolute -top-1 -right-1 w-[14px] h-[14px] rounded-full flex items-center justify-center cursor-pointer shadow-sm hover:shadow-md transition-shadow border border-[#A9A29D]"
                                      onClick={() => {
                                        const newImages = images.filter((_: any, i: number) => i !== index);
                                        form.setFieldValue(['skuList', field.name, 'images'], newImages);
                                      }}
                                    >
                                      <CloseBgIcon className="w-3 h-3 text-white" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                      })}
                      <Form.Item className="mb-0">
                        <div className="h-10 max-h-10 min-h-10 px-3 py-2 bg-white rounded-md outline outline-1 outline-borderColor flex justify-start items-center gap-3 w-fit cursor-pointer" onClick={() => add({ label: '', description: '' })}>
                          <PlusIcon className="text-primary" />
                          <div className="justify-start text-font&icon-brand text-sm font-normal font-['DM_Sans'] leading-5">{t('campaignDetail.otherInformationPreview.addProductOption')}</div>
                        </div>
                      </Form.Item>
                    </div>
                )}
              </Form.List>

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDelivery;
